import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    Divider,
    Button,
} from '@material-ui/core';
import './BidProfileSummary.scss';
import { Icon as MdiIcon } from '@mdi/react';
import { mdiShieldLockOpenOutline, mdiShieldLock } from '@mdi/js';
import {
    EmployeeReportType,
    BidProcessType,
    ShiftlineSelectionTableType,
    PeriodType,
    AnchorDay,
    ScheduleSource,
    SessionStorageKeys,
} from '@enums/index';
import { EmployeeReport, ProtectedStateIcon } from '../../components';
import ShiftLineSelectionTable from '../components/ShiftLineSelectionTable';
import TotalHoursDisplay from '../components/TotalHoursDisplay';
import EmployeeBidProfileHeader from '../components/EmployeeBidProfileHeader';
import { getReportOptions } from '../services/utilities/reports';
import {
    selectedBiddableProfile, lockAwardedShiftline, unlockAwardedShiftline,
} from '../store/bidProfile';
import { RESET_BID_SELECTION, updateShowWeeklyHours } from '../../store/bidSelection';
import { RESET_EMPLOYEE_PREFERENCES_REPORT } from '../../store/employeePreferencesReport';
import { RESET_UNAWARDED_SHIFTLINES_REPORT } from '../../store/unawardedShiftlinesReport';
import { RESET_EMPLOYEE_BIDDING_RESULTS_REPORT } from '../../store/employeeBiddingResultsReport';
import { DAY_COLUMNS_COUNT } from '../../Manager/BidProfileEdit/ScheduleSelection/ShiftlineTable/ShiftlineTable';
import LockAwardingConfirmation from '../components/LockAwardingConfirmation';

/**
 * @param {{
 *   onBack: () => void
 * }} param0
 */
function BidProfileSummary({ onBack = () => { } }) {
    const dispatch = useDispatch();
    const { t } = useTranslation('shiftBidding');

    const userInitials = sessionStorage.getItem(SessionStorageKeys.userInitials);

    /** @type {ShiftBidding.EmployeeStore} */
    // @ts-ignore
    const { employeeBidProfile: bidProfile, employeeBidSelection } = useSelector((state) => state);
    const {
        profileId,
        name,
        status: statusId,
        employeeRank,
        employeeSubmissionDate,
        requestPeriodStartDate,
        requestPeriodEndDate,
        submissionPeriodStartDate,
        submissionPeriodEndDate,
        isEnableEmployeeBidResultReport,
        isEnableEmployeeRankReport,
        bidProcessType,
        isRebidAllowed,
        anchorDay,
        sequentialSchedule,
        dayCount,
        periodType,
        weeklyHours,
        weeklyHoursByDefault,
        isForeverRequestPeriod,
        approvedShiftlineInfos,
        lockedEmployeeIds,
        employeeRankFullNames,
    } = bidProfile;

    const isLocked = approvedShiftlineInfos !== null && approvedShiftlineInfos.isLocked;
    const isLockable = approvedShiftlineInfos !== null && approvedShiftlineInfos.isLockable;

    const {
        items: bidSelectionItems,
    } = employeeBidSelection;

    const [showReport, setShowReport] = useState(EmployeeReportType.None);
    const [showProtectModal, setShowProtectModal] = useState(false);
    const isSequential = bidProcessType === BidProcessType.Sequential;
    const isPreferential = bidProcessType === BidProcessType.Preferential;

    const employeeFilters = employeeRankFullNames.map((employee) => ({
        employeeId: employee.employeeId,
        name: employee.fullName,
        rank: employee.rank,
    }));
    
    /**
     * @type {import("../../components/GroupedIconButton/GroupedIconButton").GroupedIconButtonOption[]}}
     */
    const reportOptions = getReportOptions({
        t, isSequential, isEnableEmployeeRankReport, isEnableEmployeeBidResultReport, setShowReport, employeeId: employeeRank.employeeId,
    });

    const handleEditSelection = () => {
        dispatch(selectedBiddableProfile(profileId));
    };

    const handleBack = () => {
        onBack();
        dispatch({ type: RESET_BID_SELECTION });
        dispatch({ type: RESET_EMPLOYEE_PREFERENCES_REPORT });
        dispatch({ type: RESET_UNAWARDED_SHIFTLINES_REPORT });
        dispatch({ type: RESET_EMPLOYEE_BIDDING_RESULTS_REPORT });
    };
    const handleProtectAssignedShiftline = () => {
        setShowProtectModal(true);
    };
    const confirmProtectAssignedShiftline = () => {
        if (isLocked) {
            dispatch(unlockAwardedShiftline(profileId, true));
        } else {
            dispatch(lockAwardedShiftline(profileId, true));
        }
        setShowProtectModal(false);
    };
    const approvedBid = bidSelectionItems.find((x) => x.approvedRow);

    return (
        <>
            <EmployeeBidProfileHeader
                handleBack={handleBack}
                title={name}
                statusId={statusId}
                requestPeriodStartDate={requestPeriodStartDate}
                requestPeriodEndDate={requestPeriodEndDate}
                submissionPeriodStartDate={submissionPeriodStartDate}
                submissionPeriodEndDate={submissionPeriodEndDate}
                isForeverRequestPeriod={isForeverRequestPeriod}
                employeeSubmissionDate={employeeSubmissionDate}
                reportOptions={reportOptions}
                handleEditSelection={handleEditSelection}
                employeeRank={employeeRank}
                userInitials={userInitials}
            />
            <Divider />
            {
                approvedBid ? (
                    <>
                        <div
                            className="shiftline-table-head"
                        >
                            {t('employee.summary.approvedSelection')}
                            {isLockable && <ProtectedStateIcon isProtected={isLocked} />}
                        </div>
                        <div
                            className="shiftline-table-head-end"
                        >
                            {isLockable && (
                                <Button
                                    className="bps-protect-button"
                                    variant="outlined"
                                    onClick={handleProtectAssignedShiftline}
                                >
                                    <MdiIcon
                                        path={isLocked ? mdiShieldLockOpenOutline : mdiShieldLock}
                                        size={1}
                                        style={{ paddingRight: '5px' }}
                                    />
                                    {isLocked ? 'Remove protection' : 'Protect your selection'}
                                </Button>
                            )}
                            {
                                weeklyHours && (
                                    <div className="bps-TotalHoursDisplay-container">
                                        <TotalHoursDisplay
                                            defaultValue={weeklyHoursByDefault}
                                            onChange={(checked) => updateShowWeeklyHours(checked, dispatch)}
                                        />
                                    </div>
                                )
                            }
                        </div>
                        <div>
                            <ShiftLineSelectionTable
                                isMultipleSelection={isPreferential}
                                daysPerPeriod={DAY_COLUMNS_COUNT}
                                type={ShiftlineSelectionTableType.ApprovedBidSummary}
                                externalStoreData={{
                                    filteredItems: [],
                                    shiftsDictionary: {},
                                    anchorDay: AnchorDay.Sunday,
                                    scheduleSource: ScheduleSource.ExcelFile,
                                    dayCount: 0,
                                    periodType: PeriodType.Weeks,
                                }}
                                autoHeight
                            />
                        </div>
                    </>
                ) : null
            }

            <div
                className="shiftline-table-head"
            >
                {t('employee.summary.selection')}
            </div>
            {
                bidSelectionItems?.length
                    ? (
                        <div
                            className="sl-selection-table"
                        >
                            {
                                (weeklyHours && !approvedBid) && (
                                    <div className="bps-TotalHoursDisplay-container">
                                        <TotalHoursDisplay
                                            defaultValue={weeklyHoursByDefault}
                                            onChange={(checked) => updateShowWeeklyHours(checked, dispatch)}
                                        />
                                    </div>
                                )
                            }
                            <ShiftLineSelectionTable
                                isMultipleSelection={isPreferential}
                                daysPerPeriod={DAY_COLUMNS_COUNT}
                                type={ShiftlineSelectionTableType.SelectionSummary}
                                externalStoreData={{
                                    filteredItems: [],
                                    shiftsDictionary: {},
                                    anchorDay: AnchorDay.Sunday,
                                    scheduleSource: ScheduleSource.ExcelFile,
                                    dayCount: 0,
                                    periodType: PeriodType.Weeks,
                                }}
                            />
                        </div>
                    ) : (
                        <div
                            className="shiftline-table-replacement"
                        >
                            {
                                t('employee.summary.noSelection')
                            }
                        </div>
                    )
            }
            <EmployeeReport
                profileId={profileId}
                employeeReport={showReport}
                onClose={() => setShowReport(EmployeeReportType.None)}
                dataviewEmployeeData={[]}
                filteredDataviewEmployeeData={[]}
                dataviewFields={[]}
                anchorDay={anchorDay}
                isRebid={isRebidAllowed}
                showScheduledDateAndTime={isSequential}
                sequentialSchedule={sequentialSchedule}
                dayCount={dayCount}
                isPeriodTypeDay={periodType === PeriodType.Days}
                lockedEmployeeIds={lockedEmployeeIds}
                employeeFilters={employeeFilters}
            />
            <LockAwardingConfirmation
                isOpen={showProtectModal}
                setIsOpen={setShowProtectModal}
                onConfirm={confirmProtectAssignedShiftline}
                isLocked={isLocked}
            />
        </>
    );
}

export default BidProfileSummary;
